import { useMemo } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import KontaktHero from './components/KontaktHero';
import KontaktForm from './components/KontaktForm';
import KontaktInfo from './components/KontaktInfo';
import KontaktFAQ from './components/KontaktFAQ';
import GoogleReviewQR from './components/GoogleReviewQR';
import SEO from '../../components/feature/SEO';
import JsonLd from '../../components/feature/JsonLd';

export default function KontaktPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://igm-baudienste.com';

  const jsonLdData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'ContactPage',
        '@id': `${siteUrl}/kontakt`,
        'url': `${siteUrl}/kontakt`,
        'name': 'Kontakt | IGM Facility Service Ratingen',
        'description': 'Kontaktieren Sie IGM Facility Service in Ratingen: Tel. 02102 7398070 \u2013 kostenlose Beratung & schnelle Angebote f\u00fcr Geb\u00e4udereinigung und Facility Management.',
        'isPartOf': { '@id': `${siteUrl}/#website` },
      },
      {
        '@type': 'LocalBusiness',
        '@id': `${siteUrl}/#business`,
        'name': 'IGM Facility Service',
        'url': siteUrl,
        'telephone': '+4921027398070',
        'email': 'kontakt@igmfacilityservice.de',
        'address': {
          '@type': 'PostalAddress',
          'streetAddress': 'Industriestr. 37',
          'addressLocality': 'Ratingen',
          'postalCode': '40878',
          'addressCountry': 'DE',
        },
        'openingHoursSpecification': [
          {
            '@type': 'OpeningHoursSpecification',
            'dayOfWeek': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            'opens': '10:00',
            'closes': '15:00',
          },
          {
            '@type': 'OpeningHoursSpecification',
            'dayOfWeek': 'Saturday',
            'opens': '12:00',
            'closes': '15:00',
          },
        ],
      },
      {
        '@type': 'FAQPage',
        '@id': `${siteUrl}/kontakt#faq`,
        'mainEntity': [
          {
            '@type': 'Question',
            'name': 'Wie schnell erhalte ich ein Angebot?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'In der Regel erstellen wir Ihr kostenloses Festpreisangebot innerhalb von 24 Stunden nach Ihrer Anfrage. Bei gr\u00f6\u00dferen Projekten vereinbaren wir gerne einen Vor-Ort-Termin f\u00fcr eine pr\u00e4zise Kalkulation.',
            },
          },
          {
            '@type': 'Question',
            'name': 'In welchem Gebiet sind Sie t\u00e4tig?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Wir sind haupts\u00e4chlich in der Region Rhein-Ruhr t\u00e4tig \u2013 dazu geh\u00f6ren Ratingen, D\u00fcsseldorf, Mettmann, Velbert, Wuppertal, Solingen, Remscheid und umliegende Kreise. F\u00fcr Gro\u00dfprojekte sind wir auch NRW-weit aktiv.',
            },
          },
          {
            '@type': 'Question',
            'name': 'Bieten Sie auch Einzelgewerke an?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Ja! Neben schl\u00fcsselfertigen Gesamtprojekten f\u00fchren wir auch Einzelgewerke durch \u2013 ob nur Trockenbau, Bodenbel\u00e4ge oder reine Gartenarbeiten.',
            },
          },
          {
            '@type': 'Question',
            'name': 'Gibt es eine Festpreisgarantie?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Ja. Sobald das Angebot vereinbart und unterzeichnet ist, gilt ein verbindlicher Festpreis. Keine versteckten Kosten, keine Nachforderungen \u2013 au\u00dfer bei expliziten \u00c4nderungsw\u00fcnschen Ihrerseits.',
            },
          },
          {
            '@type': 'Question',
            'name': 'Wie l\u00e4uft ein typisches Projekt ab?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Nach Ihrer Anfrage folgt eine Beratung (telefonisch oder vor Ort), dann ein schriftliches Angebot. Nach Auftragserteilung planen wir Termine, beschaffen Material und f\u00fchren die Arbeiten durch. Am Ende \u00fcbergeben wir das fertige Werk mit Abnahmeprotokoll.',
            },
          },
          {
            '@type': 'Question',
            'name': '\u00dcbernehmen Sie auch die Entsorgung von Baumaterialien?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Ja, auf Wunsch \u00fcbernehmen wir die fachgerechte Entsorgung von Abbruchmaterial, Bauschutt und Altmaterialien. Dies kann optional im Angebot ber\u00fccksichtigt werden.',
            },
          },
        ],
      },
    ],
  }), [siteUrl]);

  const breadcrumbData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': 'Home',
        'item': siteUrl,
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'name': 'Kontakt',
        'item': `${siteUrl}/kontakt`,
      },
    ],
  }), [siteUrl]);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <SEO
        title="Kontakt | IGM Facility Service Ratingen – Anfrage & Beratung"
        description="Kontaktieren Sie IGM Facility Service in Ratingen: Tel. 02102 7398070 | Bürozeiten Mo–Fr 10–15 Uhr, Sa 12–15 Uhr. Kostenlose Beratung & schnelle Angebote."
        keywords="Kontakt IGM Facility Service, Anfrage Gebäudereinigung Ratingen, Facility Management Angebot"
        canonical={`${siteUrl}/kontakt`}
      />
      <JsonLd data={jsonLdData} id="json-ld-kontakt" />
      <JsonLd data={breadcrumbData} id="json-ld-kontakt-breadcrumb" />
      <Navbar />

      <KontaktHero />

      {/* Main Content */}
      <section className="py-14 sm:py-20 bg-[#F8F7F4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1fr_520px] gap-10 xl:gap-14 items-start">
            <KontaktForm />
            <KontaktInfo />
          </div>
        </div>
      </section>

      {/* Process Strip */}
      <section className="py-12 bg-[#0D2B4E]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {[
              { step: '01', icon: 'ri-file-text-line', title: 'Anfrage senden', desc: 'Formular ausfüllen oder anrufen' },
              { step: '02', icon: 'ri-customer-service-2-line', title: 'Beratungsgespräch', desc: 'Wir klären Details & Wünsche' },
              { step: '03', icon: 'ri-price-tag-3-line', title: 'Angebot erhalten', desc: 'Transparenter Festpreis in 24h' },
              { step: '04', icon: 'ri-hammer-line', title: 'Projekt starten', desc: 'Terminvereinbarung & Umsetzung' },
            ].map((s) => (
              <div key={s.step} className="flex flex-col items-center text-center">
                <div className="relative mb-3">
                  <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
                    <i className={`${s.icon} text-2xl text-white`} />
                  </div>
                  <span className="absolute -top-1.5 -right-1.5 bg-[#C8A96E] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                    {s.step}
                  </span>
                </div>
                <p className="font-semibold text-white text-sm mb-1">{s.title}</p>
                <p className="text-white/60 text-xs">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <KontaktFAQ />

      <GoogleReviewQR />

      {/* Emergency CTA */}
      <section className="py-12 bg-[#F8F7F4] border-t border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="w-12 h-12 bg-[#0D2B4E] rounded-xl flex items-center justify-center mx-auto mb-4">
            <i className="ri-alarm-warning-line text-xl text-white" />
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Notfall oder dringende Anfrage?</h3>
          <p className="text-gray-500 text-sm mb-6">
            Bei dringenden Bauschäden oder Notfällen stehen wir auch außerhalb der Bürozeiten zur Verfügung.
          </p>
          <a
            href="tel:+4921027398070"
            className="inline-flex items-center gap-3 bg-[#0D2B4E] text-white px-8 py-4 rounded-lg font-bold text-base hover:bg-[#0a2240] transition-colors whitespace-nowrap cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-phone-fill text-lg" />
            </div>
            02102 7398070 — Jetzt anrufen
          </a>
          <p className="text-xs text-gray-400 mt-3">24/7 Notdienst verfügbar</p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
